/**
 * Planilla nicaragüense.
 *
 * Rates confirmed current for 2026 and supplied by the caller from settings, never read from
 * a constant here — re-running an old payroll has to use the rates in force at the time.
 *
 * The part that is easy to get wrong, and that this module exists to get right:
 *
 *   Aguinaldo     exempt from INSS and IR      (Art. 97 CT)
 *   Vacaciones    subject to INSS and IR
 *   Indemnización exempt from INSS and IR      (Art. 45 CT)
 *
 * Aguinaldo and vacaciones look like identical money on a payslip and are taxed in opposite
 * ways. Both accrue monthly as liabilities rather than landing as a December surprise, which
 * is what lets an October cash-flow report tell the truth about what December owes.
 *
 * Since 2026-10-12 a run pays a week, half a month or a month, and each person may be off INSS
 * or off the benefits (the owner: «we also need an option to not pay INSS»): `OpcionesDePago`.
 * Left out, a month with both, as every run was before.
 */

import {
  ZERO,
  addMoney,
  applyBp,
  centavos,
  mulDiv,
  subMoney,
  type Centavos,
} from '@abastece/shared';
import type { IrBracket } from '@abastece/shared';

export interface PayrollRates {
  inssLaboralBp: number;
  inssPatronalBp: number;
  inatecBp: number;
  irTramos: readonly IrBracket[];
  /** The shop's `vacacionesAcumulan`: false, no run sets vacations aside. Absent, they accrue. */
  vacacionesAcumulan?: boolean;
}

export interface PayrollLine {
  /** Salario ordinario del mes. */
  bruto: Centavos;
  /** Vacaciones pagadas en este período — gravables, a diferencia del aguinaldo. */
  vacacionesPagadas?: Centavos;
  /** Horas extra, comisiones y otros ingresos gravables. */
  otrosGravables?: Centavos;
  /** Aguinaldo pagado en este período — exento de INSS e IR. */
  aguinaldoPagado?: Centavos;
  /** Indemnización de ley — exenta de INSS e IR. */
  indemnizacion?: Centavos;
}

/** How the person on a line is paid: how many runs make a year, and what applies to them. */
export interface OpcionesDePago {
  /** 12 for a month, 24 for half a month, 52 for a week: what the income tax projects a year from. */
  pagosPorAnio?: number;
  /** INSS, theirs and the shop's, and INATEC. */
  conInss?: boolean;
  /** Aguinaldo and vacations accrue. */
  conPrestaciones?: boolean;
  /** What this person's earlier runs of the year paid and withheld (`irDeCorrida`); absent, none. */
  delAnio?: AcumuladoDelAnio;
  /** The part of the year this run pays, in 312ths (`partesDeCorrida`); absent, one run of `pagosPorAnio`. */
  partes?: number;
}

/**
 * What a person's earlier runs of the year paid and withheld: the income tax's base (taxable pay
 * less their INSS), the tax withheld, and the part of the year they paid, in 312ths.
 */
export interface AcumuladoDelAnio {
  base: number;
  ir: number;
  partes: number;
}

/** A year in parts that a week (6), half a month (13) and a month (26) each make whole. */
export const PARTES_DEL_ANIO = 312;

/** The part of a year one run of a schedule pays, in 312ths. */
export const partesDeCorrida = (pagosPorAnio: number): number => PARTES_DEL_ANIO / pagosPorAnio;

export interface PayrollResult {
  /** Ingreso gravable: salario + vacaciones + otros. Excluye aguinaldo e indemnización. */
  gravable: Centavos;
  /** Ingreso exento: aguinaldo + indemnización. */
  exento: Centavos;
  /** Total a mostrar en la boleta. */
  brutoTotal: Centavos;

  // Deducciones al trabajador
  inssLaboral: Centavos;
  baseIr: Centavos;
  irRetenido: Centavos;
  totalDeducciones: Centavos;
  neto: Centavos;

  // Costo patronal
  inssPatronal: Centavos;
  inatec: Centavos;
  costoPatronal: Centavos;
  /** Lo que realmente le cuesta al negocio esta persona este mes. */
  costoTotal: Centavos;

  // Provisiones mensuales
  provisionAguinaldo: Centavos;
  provisionVacaciones: Centavos;
}

/**
 * IR sobre rentas del trabajo. Tabla progresiva anual de la DGI, aplicada sobre el ingreso
 * gravable anualizado menos el INSS laboral, y luego dividida entre 12.
 *
 * Exento hasta C$100,000 al año — equivalente mensual C$8,333.33. La mayoría del personal de
 * una pulpería no retiene nada, pero un repartidor con comisión puede cruzar el tramo en un
 * buen mes, así que el cálculo tiene que existir igual.
 */
export function irMensual(baseGravableMensual: Centavos, tramos: readonly IrBracket[]): Centavos {
  return irDelPeriodo(baseGravableMensual, tramos, 12);
}

/**
 * The same for a run of any length: the period's base projected over the `pagosPorAnio` runs of a
 * year, the table applied, and the year's tax shared out over them again. A week's C$1,900 is a
 * year's C$98,800, under the exemption, as a month's C$8,233 is.
 */
export function irDelPeriodo(baseGravable: Centavos, tramos: readonly IrBracket[], pagosPorAnio: number): Centavos {
  if (baseGravable <= 0) return ZERO;
  return mulDiv(irDelAnio(centavos(baseGravable * pagosPorAnio), tramos), 1, pagosPorAnio);
}

/** The DGI's table applied to a year's base. */
function irDelAnio(baseAnual: Centavos, tramos: readonly IrBracket[]): Centavos {
  let tramo: IrBracket | undefined;
  for (const t of tramos) {
    if (baseAnual > t.desde) tramo = t;
  }
  if (!tramo || tramo.tasaBp === 0) return ZERO;

  const excedente = centavos(baseAnual - tramo.desde);
  return addMoney(centavos(tramo.base), applyBp(excedente, tramo.tasaBp));
}

/**
 * The income tax of a run, with what the year's earlier runs paid (2026-10-20), the DGI's way for
 * pay that changes: everything taxable so far this year, over the part of the year it paid, taken
 * as a year; the table applied to it; that year's tax for the part of the year paid so far, less
 * what was already withheld. With no earlier run it is `irDelPeriodo`, and a pay that never
 * changes withholds the same every run.
 *
 * Each run used to project itself alone, so a one-off came every run: C$ 12,000 of vacations paid
 * in January was taxed as C$ 144,000 more a year, and the year withheld C$ 7,044.67 against the
 * C$ 6,762 its pay owed, which nothing ever put right. Now the runs after it withhold less, and by
 * the year's last run the year has withheld the tax on what it paid. Never below nothing: what was
 * withheld of more is for the year's own reckoning, not a run's.
 */
export function irDeCorrida(
  base: Centavos,
  tramos: readonly IrBracket[],
  pagosPorAnio: number,
  antes?: AcumuladoDelAnio,
  partes: number = partesDeCorrida(pagosPorAnio),
): Centavos {
  const total = (antes?.base ?? 0) + Math.max(0, base);
  const pagadas = (antes?.partes ?? 0) + partes;
  if (total <= 0 || pagadas <= 0) return ZERO;
  const delAnio = irDelAnio(mulDiv(centavos(total), PARTES_DEL_ANIO, pagadas), tramos);
  return centavos(Math.max(0, mulDiv(delAnio, pagadas, PARTES_DEL_ANIO) - (antes?.ir ?? 0)));
}

/**
 * Provisiones sobre el salario ordinario: 1/12 de lo pagado, para ambas — un mes al año de cada una,
 * se pague por mes, por semana o por día —, y nada fuera de las prestaciones.
 */
export function provisionesDe(
  bruto: Centavos,
  conPrestaciones: boolean,
  /** The shop's vacations accrue (`vacacionesAcumulan`). */
  conVacaciones = true,
): { provisionAguinaldo: Centavos; provisionVacaciones: Centavos } {
  const parte = conPrestaciones ? mulDiv(bruto, 1, 12) : ZERO;
  return { provisionAguinaldo: parte, provisionVacaciones: conVacaciones ? parte : ZERO };
}

export function calcularPlanilla(line: PayrollLine, rates: PayrollRates, opciones: OpcionesDePago = {}): PayrollResult {
  const { pagosPorAnio = 12, conInss = true, conPrestaciones = true, delAnio, partes } = opciones;
  const vacaciones = line.vacacionesPagadas ?? ZERO;
  const otros = line.otrosGravables ?? ZERO;
  const aguinaldo = line.aguinaldoPagado ?? ZERO;
  const indemnizacion = line.indemnizacion ?? ZERO;

  const gravable = addMoney(line.bruto, vacaciones, otros);
  const exento = addMoney(aguinaldo, indemnizacion);
  const brutoTotal = addMoney(gravable, exento);

  // Off INSS, nothing of it either way, and no INATEC, which goes with it.
  const inssLaboral = conInss ? applyBp(gravable, rates.inssLaboralBp) : ZERO;
  const baseIr = subMoney(gravable, inssLaboral);
  const irRetenido = irDeCorrida(baseIr, rates.irTramos, pagosPorAnio, delAnio, partes);
  const totalDeducciones = addMoney(inssLaboral, irRetenido);
  const neto = subMoney(brutoTotal, totalDeducciones);

  const inssPatronal = conInss ? applyBp(gravable, rates.inssPatronalBp) : ZERO;
  const inatec = conInss ? applyBp(gravable, rates.inatecBp) : ZERO;
  const costoPatronal = addMoney(inssPatronal, inatec);

  const { provisionAguinaldo, provisionVacaciones } = provisionesDe(line.bruto, conPrestaciones, rates.vacacionesAcumulan !== false);

  return {
    gravable,
    exento,
    brutoTotal,
    inssLaboral,
    baseIr,
    irRetenido,
    totalDeducciones,
    neto,
    inssPatronal,
    inatec,
    costoPatronal,
    costoTotal: addMoney(brutoTotal, costoPatronal),
    provisionAguinaldo,
    provisionVacaciones,
  };
}

/**
 * Aguinaldo / décimo tercer mes — Arts. 93–97 CT.
 *
 * Salario mensual ÷ 12 × meses trabajados en el período. El período corre del 1 de diciembre
 * al 30 de noviembre, y el pago vence dentro de los primeros 10 días de diciembre.
 */
export function calcularAguinaldo(salarioMensual: Centavos, mesesTrabajados: number): Centavos {
  const meses = Math.max(0, Math.min(12, mesesTrabajados));
  return mulDiv(salarioMensual, Math.round(meses * 1000), 12_000);
}

/**
 * Days from one date to another, both in, the way the law counts a month's pay: thirty to every
 * month, its last day its thirtieth, so a whole February is thirty days and a whole year 360.
 */
export function dias360(desde: string, hasta: string): number {
  const [a1, m1, d1] = desde.split('-').map(Number) as [number, number, number];
  const [a2, m2, d2] = hasta.split('-').map(Number) as [number, number, number];
  const ultimoDe = (a: number, m: number) => new Date(Date.UTC(a, m, 0)).getUTCDate();
  const dia1 = d1 === ultimoDe(a1, m1) ? 30 : Math.min(d1, 30);
  const dia2 = d2 === ultimoDe(a2, m2) ? 30 : Math.min(d2, 30);
  return (a2 - a1) * 360 + (m2 - m1) * 30 + (dia2 - dia1) + 1;
}

/** The first day of the aguinaldo period a day falls in: 1 December of its year, or of the year before. */
export function inicioDelAguinaldo(fecha: string): string {
  const anio = Number(fecha.slice(0, 4));
  return `${fecha.slice(5, 7) === '12' ? anio : anio - 1}-12-01`;
}

/** Meses trabajados dentro del período de aguinaldo (1 dic – 30 nov) a una fecha dada. */
export function mesesDeAguinaldo(ingreso: Date, corte: Date): number {
  const inicioPeriodo = new Date(
    Date.UTC(corte.getUTCMonth() >= 11 ? corte.getUTCFullYear() : corte.getUTCFullYear() - 1, 11, 1),
  );
  const desde = ingreso > inicioPeriodo ? ingreso : inicioPeriodo;
  if (corte <= desde) return 0;
  const dias = (corte.getTime() - desde.getTime()) / 86_400_000;
  return Math.min(12, dias / 30.4375);
}

/**
 * Indemnización por antigüedad — Art. 45 CT.
 *
 * Un mes de salario por cada uno de los primeros 3 años, 20 días por cada año adicional,
 * con tope de 5 meses. Procede por despido sin causa justa, renuncia con 15 días de preaviso
 * escrito, o mutuo acuerdo. Exenta de INSS e IR.
 */
export function calcularIndemnizacion(salarioMensual: Centavos, anios: number): Centavos {
  if (anios <= 0) return ZERO;

  /**
   * Computed in days, not in months.
   *
   * "20 días" is exact; two thirds of a month is not. Scaling 3.6667 months into an integer
   * ratio puts a 40-centavo error into a severance payment — which is both wrong and the kind
   * of wrong an employee will notice and dispute.
   */
  const DIAS_POR_MES = 30;
  const TOPE_DIAS = 5 * DIAS_POR_MES;

  const primeros = Math.min(anios, 3);
  let dias = primeros * DIAS_POR_MES;
  if (anios > 3) dias += (anios - 3) * 20;

  const diasFinales = Math.min(dias, TOPE_DIAS);
  const diasMilli = Math.round(diasFinales * 1_000);
  return mulDiv(salarioMensual, diasMilli, DIAS_POR_MES * 1_000);
}

/** Vacaciones acumuladas: 2.5 días por mes, 30 días al año — Art. 76 CT. */
export function diasVacacionesAcumulados(mesesTrabajados: number): number {
  return Math.max(0, mesesTrabajados) * 2.5;
}

/**
 * Vacation days one run earns, in thousandths: thirty a year, so 2.5 a month, 1.25 a half month,
 * 0.577 a week; for somebody paid by the day, a twelfth of the days worked — thirty for every 360.
 */
export function diasDeVacacionesGanados(pagosPorAnio: number, diasTrabajados?: number): number {
  if (diasTrabajados !== undefined) return Math.round(diasTrabajados / 12);
  return Math.round(30_000 / pagosPorAnio);
}

export function valorDiaVacacion(salarioMensual: Centavos): Centavos {
  return mulDiv(salarioMensual, 1, 30);
}

/** Vacation days, in thousandths, at a monthly salary: each a thirtieth of it (Art. 76 CT). */
export function valorDeVacaciones(salarioMensual: Centavos, diasMilli: number): Centavos {
  return mulDiv(salarioMensual, Math.max(0, diasMilli), 30_000);
}
